import { DEFAULT_WALL_COLOR } from "../components/map/defines";
import type { Shape } from "../types";

/** Only merge a pillar with a coincident four-corner enclosure, never its room. */
export function fillImportedPillars(shapes: Shape[]): Shape[] {
  const removed = new Set<string>();
  const filled = new Set<string>();
  for (const pillar of shapes) {
    if (pillar.type !== "rectangle" || pillar.layerLabel !== "pillar") continue;
    const a = ((pillar.rotation || 0) * Math.PI) / 180;
    const corners = [
      [0, 0],
      [pillar.width, 0],
      [pillar.width, pillar.height],
      [0, pillar.height]
    ].map(([x, y]) => ({
      x: pillar.x + x * Math.cos(a) - y * Math.sin(a),
      y: pillar.y + x * Math.sin(a) + y * Math.cos(a)
    }));
    const path = shapes.find((s) => {
      if (
        s.type !== "wall-path" ||
        !s.closed ||
        s.vertices.length !== 4 ||
        s.edges.some((e) => e.doors?.length || e.windows?.length || e.curvature)
      )
        return false;
      const area =
        Math.abs(
          s.vertices.reduce((sum, p, i) => {
            const q = s.vertices[(i + 1) % 4];
            return sum + p.x * q.y - q.x * p.y;
          }, 0)
        ) / 2;
      if (Math.abs(area / (pillar.width * pillar.height) - 1) > 0.3)
        return false;
      const tolerance = Math.max(
        1,
        Math.min(
          Math.min(pillar.width, pillar.height) * 0.2,
          s.thickness / 2 + 1
        )
      );
      return corners.every((p) =>
        s.vertices.some((q) => Math.hypot(p.x - q.x, p.y - q.y) <= tolerance)
      );
    });
    if (path) {
      removed.add(pillar.id);
      filled.add(path.id);
    }
  }
  return shapes
    .filter((s) => !removed.has(s.id))
    .map((s) =>
      s.type === "wall-path" && filled.has(s.id)
        ? {
            ...s,
            layerLabel: "Pillar",
            fillColor: DEFAULT_WALL_COLOR,
            fillOpacity: 1
          }
        : s
    );
}
